import Vue from 'vue'
import { UPDATE_STORE_PARAMS, SCROLLBAR_POSITION } from '../config'
import { getColumnsVisible, getRowKeyIter } from './config/utils'

import { createFlatColumns, createColumns } from './config/columns'
import { createTableRenderData, formatTableFlatData } from './config/data'
import { createHelperLine } from './config/helper-line'
import { createSelection } from './config/selection'
import { TABLE_MODE } from '../config/table.js'

// mixins
import checkbox from './modules/checkbox'
import collapse from './modules/collapse'
import filter from './modules/filter'
import sortable from './modules/sortable'
import helperLine from './modules/helper-line'
import expand from './modules/expand'
import virtual from './modules/virtual'
import mergeCell from './modules/merge-cell'
import radio from './modules/radio'
import fixedRows from './modules/fixed-rows'
import footerRows from './modules/footer-rows'

const borderContainerSize = 2 // 两个border + affter伪类

const TableStore = Vue.extend({
  mixins: [
    checkbox,
    collapse,
    filter,
    sortable,
    helperLine,
    expand,
    virtual,
    mergeCell,
    radio,
    fixedRows,
    footerRows
  ],
  data() {
    return {
      states: {
        // 全局配置
        rowKey: null,
        rowHeight: 0,
        loadingRow: [],
        footerHeight: 0,
        fixedData: [],
        columns: [],
        visibleFlatColumns: [],
        mainColumns: [],
        fixedColumnsOnLeft: [],
        fixedColumnsOnRight: [],
        columnsTotalWidth: 0,
        isAutoResizer: false,
        border: false,
        selectdInfo: {
          currentRow: null,
          currentRowIndex: null,
          renderDataIndex: null
        },
        height: 0,
        width: 0,
        maxWidth: 0,
        maxHeight: 0,
        headerHeight: 0,
        maxLevel: 1,
        estimatedRowHeight: 0,

        // 滚动相关
        scrollOffsetX: 0,
        scrollOffsetY: 0,
        scrollPos: {
          scrollLeft: 0,
          scrollTop: 0,
        },
        scrollbarPosition: SCROLLBAR_POSITION[0],
        updateRequested: true,
        // 渲染相关
        tableDataLength: 0, // 总数据长度
        renderData: [], // 渲染的数据
        cacheSize: 2,

        // 滚动条
        hScrollbarSize: 6,
        vScrollbarSize: 6,
        hScrollbarShow: false,
        vScrollbarShow: false,
      },
    }
  },
  computed: {
    leftTableWidth() {
      return getColumnsVisible(this.states.fixedColumnsOnLeft).reduce(
        (acc, cur) => acc + cur.width,
        0
      )
    },
    rightTableWidth() {
      return getColumnsVisible(this.states.fixedColumnsOnRight).reduce(
        (acc, cur) => acc + cur.width,
        0
      )
    },
    mainColumnsTotalWidth() {
      return getColumnsVisible(this.states.mainColumns).reduce(
        (acc, cur) => acc + (cur.width || 0),
        0
      )
    },
    rowTotalHeight() {
      const enableDynamicHeight = this.usingDynamicHeight
      return enableDynamicHeight
        ? this.getEstimatedTotalHeight()
        : Math.max(0, this.states.tableDataLength * this.states.rowHeight)
    },
    headerTotalHeight() {
      return Math.max(0, this.states.maxLevel * this.states.headerHeight)
    },
    fixedRowsTotalHeight() {
      return (
        this.states.rowHeight *
        (this.table.$props.fixedRowsTop ||
          (this.table.$props.fixedRowsData || []).length ||
          0)
      )
    },
    footerTotalHeight() {
      const footerRows = this.table.$props.footerRows || []
      const leng = footerRows.length
      const [defaultSolt] = this.table.$slots.default || []
      const isFooterSolt =
        (this.table.$slots && this.table.$slots.footer) ||
        (defaultSolt && defaultSolt.footer)
      return leng && !isFooterSolt
        ? this.states.rowHeight * leng
        : this.states.footerHeight
    },
    tableBodyHeight() {
      let { height = 0, border } = this.states
      // 这里是在一个页面上不配置高度，但是因为有多个表格在页面排下来导致高度变成负数（window.innerHeight - dis）,走到这里意味着数据有多少表格就会多长
      if (height < 0) {
        height =
          this.headerTotalHeight + this.rowTotalHeight + borderContainerSize
      }
      const maxBodyHeight = height - this.headerTotalHeight - this.fixedRowsTotalHeight - this.footerTotalHeight
      let _tableBodyHeight = Math.max(0, maxBodyHeight - (border ? borderContainerSize : 0)) || 0
      // 空数据只展示单行高度
      if (this.rowTotalHeight === 0) return this.states.rowHeight
      // 继承的时候需要去掉滚动条的高度
      if (this.states.hScrollbarShow && this.table.$props.mode === TABLE_MODE.INHERIT) {
        _tableBodyHeight -= 12
      }
      // 如果总行高度小于最大盒子高度，就使用总行高度
      return this.rowTotalHeight > 0 && maxBodyHeight > this.rowTotalHeight
        ? this.rowTotalHeight
        : _tableBodyHeight
    },
    tableHeight() {
      const { border } = this.states
      return Math.max(
        0,
        this.tableBodyHeight +
          this.headerTotalHeight +
          this.fixedRowsTotalHeight +
          this.footerTotalHeight +
          (border ? borderContainerSize : 0)
      )
    },
    tableWidth() {
      const { width } = this.states
      return width
    },
  },
})

TableStore.prototype.initStoreStates = function(initialState = {}) {
  for (let prop in initialState) {
    if (initialState.hasOwnProperty(prop) && this.states.hasOwnProperty(prop)) {
      this.states[prop] = initialState[prop]
    }
  }
}


TableStore.prototype.updateTableData = function({
  isResetScroll = true,
  isUpdateTableRenderData = true,
} = {}) {
  const { data: propsTableData, autoResetScrollTop } = this.table.$props
  const dataInstanceChanged = propsTableData && this.tableData !== propsTableData
  // 如果数据切换，则需要判断是否保留之前的
  if (dataInstanceChanged) {
    this.resetConfig()
  }

  const {
    data,
    _tableData,
    _checkboxData = [],
    tableDataLength,
    depthMap: newDepthMap
  } = createTableRenderData({
    data: propsTableData,
    table: this.table,
    store: this,
    dataInstanceChanged
  })
  if (!this.selection) {
    this.newSelection()
  }
  if (this.tableDataLength !== tableDataLength) {
    this._curMaxIndexVal = tableDataLength
    this.commit(UPDATE_STORE_PARAMS, { tableDataLength })
    // virtualInstance 可能不存在-初始化时
    this.virtualInstance &&
      this.virtualInstance.updateParam(
        'uniqueIds',
        _tableData.map(item => String(getRowKeyIter(item, this.states.rowKey)))
      )
    // 处理当数据变少或变多需要更新表格高度
    const { maxHeight } = this.states
    const { tableBodyHeight } = this
    if (this.rowTotalHeight > tableBodyHeight && tableBodyHeight < maxHeight) {
      this.commit(UPDATE_STORE_PARAMS, { height: maxHeight })
    }
  }
  // 不参与响应`
  Object.assign(this, {
    tableData: data,
    _tableData,
    depthMap: newDepthMap,
    _checkboxData
  })
  // 重置滚动条
  if ((isResetScroll && autoResetScrollTop)) {
    Vue.nextTick(() => {
      this.resetScroll()
      if (this.usingRowColMerge) {
        // 重置虚拟列表。否则可能在偏移量计算的结果值相同时不会触发虚拟列表算法的回调。导致补全逻辑未执行。
        this.updateVirtualRange()
      }
    })
  }
  isUpdateTableRenderData && this.updateTableRenderData()
  if (this.usingDynamicHeight) {
    this.updateVirtualRange()
  } else {
    this.virtualInstance && this.updateVirtualConfig()
  }
  // 保留历史勾选时，需要触发多选框更新
  if (this.table.$props.reserveSelection) {
    this.resetSelectionUpdate()
  }
}

TableStore.prototype.resetConfig = function () {
  // 重置勾选数据
  if (!this.table.$props.reserveSelection) {
    this.resetSelectionValue()
  }
  this.resetSelectionUpdate()
}

TableStore.prototype.resetSelectionUpdate = function () {
  // 触发视图勾选更新
  this.updateSelectCheckbox()
  // 触发全选
  this.updateAllSelected()
  // 清除历史展开数据
  this.clearExpandedRowKeys()
}

TableStore.prototype.updateTableRenderData = function () {
  let { start, end } = this.states.verticalVirtualRange
  const { _tableData } = this
  this.commit(UPDATE_STORE_PARAMS, {
    renderData: _tableData.slice(start, end + 1) // slice方法原因，需要手动+1
  })
  formatTableFlatData(_tableData, this)
}

TableStore.prototype.updateVirtualRange = function () {
  const { rowHeight, cacheSize } = this.states
  const { _tableData, tableBodyHeight } = this
  this.createVerticalVirtual({
    tableBodyHeight,
    rowHeight,
    tableData: _tableData,
    cacheSize,
    rowKey: this.states.rowKey,
  })
}
TableStore.prototype.resetScroll = function ({
  scrollTop = 0,
  scrollLeft = 0,
} = {}) {
  this.table.scrollTo({ scrollTop, scrollLeft })
}
TableStore.prototype.updateVirtualConfig = function () {
  const { scrollPos, cacheSize, rowHeight } = this.states
  const { tableBodyHeight } = this
  const _kepps = Math.floor(tableBodyHeight / rowHeight)
  this.virtualInstance.updateParam('keeps', _kepps + cacheSize || 0)
  this.virtualInstance.handleScroll(scrollPos.scrollTop)
  this.horizontalVirtual && this.horizontalVirtual.handleScroll(scrollPos.scrollLeft)
}

TableStore.prototype.flatColumns = function () {
  const { columns } = this.states
  const { showIndex, showSelection, showRadio, commonColumns } = this.table.$props
  const { collapsedMap, visibleFlatColumns } = createFlatColumns({
    columns,
    showIndex,
    showSelection,
    showRadio,
    commonColumns
  })
  this.commit(UPDATE_STORE_PARAMS, {
    collapsedMap,
    visibleFlatColumns,
  })
}

TableStore.prototype.updateColumns = function () {
  const {
    visibleFlatColumns,
    isAutoResizer,
    collapsedMap,
    maxWidth,
  } = this.states
  const {
    mainColumns,
    fixedColumnsOnLeft,
    fixedColumnsOnRight,
    maxLevel,
    width: tableWidth,
  } = createColumns({
    visibleFlatColumns,
    isAutoResizer,
    collapsedMap,
    maxWidth,
    tableWidth: this.tableWidth,
    curMaxIndexVal: this._curMaxIndexVal,
    createFiltersMap: this.createFiltersMap
  })

  this.commit(UPDATE_STORE_PARAMS, {
    mainColumns,
    fixedColumnsOnLeft,
    fixedColumnsOnRight,
    maxLevel,
    width: tableWidth,
  })

  this.createHorizontalVirtual(mainColumns)
}

TableStore.prototype.updateHelperLine = function () {
  const { border } = this.table.$props
  const { verHelperLine } = createHelperLine({ border })
  this.commit(UPDATE_STORE_PARAMS, {
    verHelperLine,
  })
}

TableStore.prototype.setSelectedInRows = function (defaultSelectedRowKeys) {
  const tableDataLength = this.tableData.length
  if (!this.selection) {
    this.newSelection()
  }
  const {
    isAllSelected,
    selectOnIndeterminate,
    selection
  } = createSelection({ tableDataLength, defaultSelectedRowKeys, reserveSelection: this.table.$props.reserveSelection, selection: this.selection })
  this.setSelection(selection)
  // 更新状态，事件抛出
  this.updateAllSelected()
  this.updateSelectCheckbox()
  this.commit(UPDATE_STORE_PARAMS, {
    isAllSelected,
    selectOnIndeterminate,
  })
}

TableStore.prototype.mutations = {
  [UPDATE_STORE_PARAMS](state, data = {}) {
    for (let prop in data) {
      if (data.hasOwnProperty(prop) && state.hasOwnProperty(prop)) {
        state[prop] = data[prop]
      }
    }
  }
}
TableStore.prototype.commit = function (name, ...args) {
  const mutations = this.mutations
  if (mutations[name]) {
    mutations[name].apply(this, [this.states].concat(args))
  } else {
    throw new Error(`Action not found: ${name}`)
  }
}

export default TableStore



// WEBPACK FOOTER //
// ./src/public/components/kye-table2/store/table-store.js